/**
 * Add / Edit Admin User Component
 * ------------------------------------
 * Handles:
 * - Creating new admin users
 * - Editing existing admin users
 * - Updating verification status
 * - Managing order types (multi-select)
 * - Resetting form and clearing edit mode
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    TextField,
    Button,
    Chip,
    Box,
    Grid,
    FormControlLabel,
    Switch,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Typography,
    InputAdornment,
    useTheme,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    TablePagination,
} from "@mui/material";

import AdminApi from "Service/Admin.api";
import { StyledTableCell, StyledTableRow, HeaderCard, SearchCard, StyledContainer } from "./ProfessionalUserList";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { formatUserType } from "Utils/Common";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import { CardContent, Fade, Slide } from "@mui/material";
import { IndianStates } from "../../Constants/Arrays";

/* ------------------------------
   INTERFACES
------------------------------ */
interface OrderType {
    id: number;
    name: string;
}

interface UserFormType {
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
    bverified: boolean;
    beProfessionalVerified: boolean;
    userType?: string;
}



interface userType {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
    bverified?: boolean;
    beProfessionalVerified?: boolean;
    userType?: string;
}

/* ------------------------------
   MAIN COMPONENT
------------------------------ */

const AddAdminUser: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    /* ----------------------------------
       LOCAL STATE
    ---------------------------------- */
    const [formData, setFormData] = useState<UserFormType>({
        bverified: false, // Default verified OFF for new admins
        firstName: "",
        lastName: "",
        email: "",
        contactNumber: "",
        beProfessionalVerified: false,
        userType: "",
    });

    const [adminList, setAdminList] = useState<userType[]>([]);
    const [editId, setEditId] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRole, setSelectedRole] = useState("All");
    const containerRef = useRef<HTMLDivElement>(null);

    // Document Modal State
    const [openDocsModal, setOpenDocsModal] = useState(false);
    const [selectedUserDocs, setSelectedUserDocs] = useState<any[]>([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [docUserId, setDocUserId] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);

    // User Details Modal State
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null);

    const handleViewDetails = (user: any) => {
        setSelectedUserDetails(user);
        setOpenDetailsModal(true);
    };

    const handleSendReminder = async (userId: number) => {
        try {
            dispatch(startLoading({ loading: true, loadtext: "Sending reminder email..." }));
            const res = await AdminApi.sendProfileReminderEmail(userId);
            if (res?.success) {
                dispatch(setAlertData({ openAlert: true, alertMessage: "Reminder email sent successfully!", alertVariant: "success" }));
            } else {
                dispatch(setAlertData({ openAlert: true, alertMessage: res?.message || "Failed to send email", alertVariant: "error" }));
            }
        } catch (error) {
            dispatch(setAlertData({ openAlert: true, alertMessage: "Failed to send email", alertVariant: "error" }));
        } finally {
            dispatch(stopLoading());
        }
    };

    const formatKey = (key: string) => {
        if (key.toLowerCase() === "id") return "User ID";
        if (key === "bverified") return "General Verification";
        if (key === "beProfessionalVerified") return "Professional Verification";
        if (key === "profilePicturePath") return "Profile Picture";
        if (key.toLowerCase() === "isprofessionalagreementaccepted") return "Professional Agreement";
        if (key.toLowerCase() === "isodragreementaccepted") return "ODR Agreement";
        if (key.toLowerCase() === "corporateorg") return "Corporate Organization";
        return key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase());
    };

    const renderValue = (key: string, value: any) => {
        if (value === null || value === undefined || value === "") return "N/A";

        // Date Formatting
        if (key.toLowerCase().includes("date") || key === "createdAt" || key === "uploadedAt") {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                }
            } catch (e) { }
        }

        if (typeof value === "boolean") {
            if (key.toLowerCase().includes("verified") || key === "bverified" || key === "beProfessionalVerified" || key.toLowerCase().includes("agreement")) {
                const isAgreement = key.toLowerCase().includes("agreement");
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            label={value ? (isAgreement ? "Accepted" : "Verified") : "Pending"}
                            color={value ? "success" : "warning"}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                        />
                        {!value && key === "beProfessionalVerified" && selectedUserDetails?.userType?.toUpperCase() === 'PROFESSIONAL' && (
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleSendReminder(selectedUserDetails.id)}
                            >
                                Send Email
                            </Button>
                        )}
                    </Box>
                );
            }
            return value ? "Yes" : "No";
        }

        if (key === "profilePicturePath" || key.toLowerCase().includes("picture") || key.toLowerCase().includes("image")) {
            const baseUrl = process.env.REACT_APP_BASE_URL || '';
            const imgUrl = String(value).startsWith('http') ? String(value) : `${baseUrl}${String(value).startsWith('/') ? '' : '/'}${value}`;
            return <img src={imgUrl} alt="Profile" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                if (value.length === 0) return "None";
                if (typeof value[0] !== "object") {
                    return <Box display="flex" flexWrap="wrap" gap={1}>{value.map((v, i) => <Chip key={i} label={String(v)} size="small" />)}</Box>;
                } else {
                    return (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            {value.map((obj, index) => {
                                // Special handling for Skills
                                if (key.toLowerCase() === "skills") {
                                    return (
                                        <Grid size={{ xs: 12 }} key={index}>
                                            <Paper elevation={1} sx={{ p: 2, height: '100%', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>{obj.skillName}</Typography>
                                                <Box display="flex" flexDirection="column" gap={0.5}>
                                                    <Typography variant="body2"><strong>Experience:</strong> {obj.numberOfExperienceYears} years</Typography>
                                                    {/* <Typography variant="body2"><strong>Rating:</strong> {obj.rating}/10</Typography> */}
                                                    {/* <Typography variant="body2"><strong>Fees per call:</strong> ₹{obj.professionalFeesPerCallingRs}</Typography> */}
                                                    <Typography variant="body2" sx={{ display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }} title={obj.description}>
                                                        <strong>Description:</strong> {obj.description || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="body2"><strong>Serves Pan India:</strong> {obj.servesPanIndia ? 'Yes' : 'No'}</Typography>
                                                    {!obj.servesPanIndia && obj.servesStates && (
                                                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                                            {obj.servesStates.split(',').map((st: string) => {
                                                                const stateCode = st.trim();
                                                                const stateObj = IndianStates.find(s => s.value === stateCode);
                                                                return <Chip size="small" key={stateCode} label={stateObj ? stateObj.label : stateCode} />;
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    );
                                }

                                // Default Object Fallback
                                return (
                                    <Grid size={{ xs: 12 }} key={index}>
                                        <Paper elevation={1} sx={{ p: 1.5, mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">{obj.skillName || obj.file || `Item ${index + 1}`}</Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {Object.entries(obj)
                                                    .filter(([k]) => !k.toLowerCase().includes('pk') && !k.toLowerCase().includes('applicationuserid') && k !== 'skillName' && k !== 'file')
                                                    .map(([k, v]) => `${k.replace(/([A-Z])/g, ' $1').trim()}: ${v}`)
                                                    .join(' | ')}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    );
                }
            }
            if (key.toLowerCase() === "corporateorgdetails") {
                return (
                    <Paper elevation={1} sx={{ p: 1.5, mt: 0.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <Typography variant="body2"><strong>Name:</strong> {value.sname || value.Sname || 'N/A'}</Typography>
                        <Typography variant="body2"><strong>City:</strong> {value.city || 'N/A'}</Typography>
                        <Typography variant="body2"><strong>State:</strong> {value.state || 'N/A'}</Typography>
                        <Typography variant="body2"><strong>Address:</strong> {value.address || 'N/A'}</Typography>
                        {value.created && <Typography variant="body2"><strong>Joined:</strong> {new Date(value.created).toLocaleDateString()}</Typography>}
                    </Paper>
                );
            }
            return JSON.stringify(value);
        }

        return String(value);
    };

    const theme = useTheme();
    const { myInfo } = useSelector((state: any) => state.user);

    /* ----------------------------------
       EFFECTS - Load Data
    ---------------------------------- */
    useEffect(() => {
        // Reset to page 0 if role or search changes; otherwise just fetch
        setPage(0);
    }, [selectedRole, searchQuery]);

    useEffect(() => {
        // Debounce search query logic slightly if necessary, but we'll fetch explicitly
        const delayDebounceFn = setTimeout(() => {
            loadAllUsers(0, pageSize); // Reset to page 0 on any dependency trigger
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, selectedRole]);

    useEffect(() => {
        loadAllUsers(page, pageSize);
    }, [page, pageSize]);

    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        setIsSearching(true);
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setIsSearching(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const displayedAdmins = useMemo(() => {
        if (!Array.isArray(adminList)) return [];
        return adminList.filter((adminItem: any) => {
            const query = debouncedSearch.toLowerCase();
            const matchesSearch =
                (adminItem.firstName || "").toLowerCase().includes(query) ||
                (adminItem.lastName || "").toLowerCase().includes(query) ||
                (adminItem.email || "").toLowerCase().includes(query) ||
                (adminItem.contactNumber || "").toLowerCase().includes(query);

            const matchesRole =
                selectedRole === "All" ||
                (adminItem.userType && adminItem.userType.toUpperCase() === selectedRole.toUpperCase());

            return matchesSearch && matchesRole;
        });
    }, [adminList, debouncedSearch, selectedRole]);

    /* ----------------------------------
       Fetch Admin List
    ---------------------------------- */
    const loadAllUsers = async (apiPage: number, apiPageSize: number) => {
        dispatch(startLoading({ loading: true, loadtext: "Loading users..." }));
        try {
            // Map MUI "All" to empty
            const fetchRole = selectedRole === "All" ? undefined : selectedRole;
            const res = await AdminApi.getAllUsers(fetchRole, apiPage + 1, apiPageSize);

            if (res.data) {
                // If using our new pagination wrapper, `res.data` will have `data` and `totalRecords`
                if (res.data.data && Array.isArray(res.data.data)) {
                    setAdminList(res.data.data);
                    setTotalRecords(res.data.totalRecords || 0);
                } else if (Array.isArray(res.data)) {
                    setAdminList(res.data);
                    setTotalRecords(res.data.length);
                } else {
                    setAdminList([]);
                    setTotalRecords(0);
                }
            } else {
                setAdminList([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            dispatch(
                setAlertData({
                    alertMessage: "Error fetching users",
                    alertVariant: "error",
                    openAlert: true,
                })
            );
        }
        finally {
            dispatch(stopLoading());
        }
    };

    /* ----------------------------------
       Handle Basic Form Input Change
    ---------------------------------- */
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };



    /* ----------------------------------
       Verified Toggle
    ---------------------------------- */
    const handleVerifiedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            bverified: event.target.checked,
        }));
    };

    const handleProfessionalVerifiedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            beProfessionalVerified: event.target.checked,
        }));
    };

    /* ----------------------------------
       Reset Form (Reusable)
    ---------------------------------- */
    const handleReset = () => {
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            contactNumber: "",
            bverified: formData.bverified ?? false,
            beProfessionalVerified: formData.beProfessionalVerified ?? false,
            userType: "",
        });
        setEditId(null);
    };

    /* ----------------------------------
       Submit Form (Create / Update)
    ---------------------------------- */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        dispatch(startLoading({ loading: true, loadtext: "Updating verified status..." }));
        try {
            if (editId) {
                await AdminApi.updateUserVerifiedStatus({
                    "userId": editId,
                    "bVerified": formData.bverified,
                    "beProfessionalVerified": formData.beProfessionalVerified
                });

                dispatch(stopLoading());
                dispatch(
                    setAlertData({
                        alertMessage: "Verified Status Updated Successfully",
                        alertVariant: "success",
                        openAlert: true,
                    })
                );
            }
            await loadAllUsers(page, pageSize);
            handleReset();
        } catch (error) {
            console.error("Error saving admin:", error);
            dispatch(
                setAlertData({
                    alertMessage: "Error Updating Verified Status",
                    alertVariant: "error",
                    openAlert: true,
                })
            );
        }

    };

    /* ----------------------------------
       Handle Edit (Prefill Form)
    ---------------------------------- */
    const handleEdit = (item: userType) => {
        setEditId(item.id);

        setFormData({
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
            contactNumber: item.contactNumber,
            bverified: item.bverified ?? false,
            beProfessionalVerified: item.beProfessionalVerified ?? false,
            userType: item.userType,
            // verified: item.verified ?? true,
        });

        // scroll top
        if (containerRef.current) {
            containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }

    };

    /* ----------------------------------
       Handle Document Modal
    ---------------------------------- */
    const handleViewDocuments = async (user: userType) => {
        setDocUserId(user.id);
        setOpenDocsModal(true);
        setLoadingDocs(true);
        try {
            const response = await AdminApi.getProfile(String(user.id));
            if (response?.data?.professionalCertificates) {
                setSelectedUserDocs(response.data.professionalCertificates);
            } else {
                setSelectedUserDocs([]);
            }
        } catch (error) {
            console.error("Error fetching professional documents:", error);
            dispatch(setAlertData({
                alertMessage: "Error fetching documents",
                alertVariant: "error",
                openAlert: true,
            }));
        } finally {
            setLoadingDocs(false);
        }
    };

    const handleDeleteDocument = async (docId: number) => {
        if (!window.confirm("Are you sure you want to delete this document?")) return;

        dispatch(startLoading({ loading: true, loadtext: "Deleting document..." }));
        try {
            const res = await AdminApi.deleteProfessionalCertificate(docId);
            if (res.success) {
                dispatch(setAlertData({
                    alertMessage: "Document deleted successfully",
                    alertVariant: "success",
                    openAlert: true,
                }));
                // Refresh docs list
                setSelectedUserDocs(prev => prev.filter(d => d.pkProfessionalCertificate !== docId));
            }
        } catch (error) {
            console.error("Error deleting document:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    /* ----------------------------------
       RENDER
    ---------------------------------- */
    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box ref={containerRef} sx={{ width: '100%', overflow: "auto", maxHeight: "90vh", pb: 10 }}>
                    {/* Back Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Back
                        </Button>
                    </Box>

                    {/* Header Section */}
                    <HeaderCard elevation={8} sx={{ mb: 4 }}>
                        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexDirection={{ xs: 'column', md: 'row' }}
                                gap={2}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <GroupIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                                    <Box>
                                        <Typography
                                            variant={theme.breakpoints.down('sm') ? "h5" : "h4"}
                                            component="h1"
                                            fontWeight="bold"
                                            color="white"
                                            gutterBottom
                                        >
                                            All User Management
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ opacity: 0.9 }}
                                            color='white'
                                        >
                                            Manage and verify all user types from a single dashboard
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </HeaderCard>

                    {/* Edit Form (Conditional) */}
                    {editId && (
                        <Slide direction="down" in={!!editId} mountOnEnter unmountOnExit>
                            <Box sx={{ mb: 4 }}>
                                <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                                    <Typography variant="h6" gutterBottom textAlign="center" sx={{ mb: 3 }}>
                                        Update User Verification
                                    </Typography>
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={3}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField fullWidth label="First Name" value={formData.firstName} inputProps={{ readOnly: true }} />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField fullWidth label="Last Name" value={formData.lastName} inputProps={{ readOnly: true }} />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField fullWidth label="Email" value={formData.email} inputProps={{ readOnly: true }} />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField fullWidth label="Contact Number" value={formData.contactNumber} inputProps={{ readOnly: true }} />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <FormControlLabel
                                                    control={<Switch checked={formData.bverified} onChange={handleVerifiedToggle} color="success" />}
                                                    label={formData.bverified ? "Verified" : "Not Verified"}
                                                />
                                            </Grid>
                                            {formData.userType?.toUpperCase() === 'PROFESSIONAL' && (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <FormControlLabel
                                                        control={<Switch checked={formData.beProfessionalVerified} onChange={handleProfessionalVerifiedToggle} color="success" />}
                                                        label={formData.beProfessionalVerified ? "Professional Verified" : "Professional Not Verified"}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid size={{ xs: 12 }}>
                                                <Box display="flex" gap={2}>
                                                    <Button type="submit" variant="contained" fullWidth sx={{ py: 1.5 }}>
                                                        Update Status
                                                    </Button>
                                                    <Button variant="outlined" fullWidth onClick={handleReset}>
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </Paper>
                            </Box>
                        </Slide>
                    )}

                    {/* Search and Filters */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: myInfo?.userType === "SUPERADMIN" ? 5 : 8 }}>
                            <SearchCard elevation={2}>
                                <CardContent sx={{ p: '16px !important' }}>
                                    <TextField
                                        fullWidth
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search User..."
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon color="primary" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: isSearching && (
                                                    <InputAdornment position="end">
                                                        <CircularProgress size={20} />
                                                    </InputAdornment>
                                                )
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 3,
                                            },
                                        }}
                                    />
                                </CardContent>
                            </SearchCard>
                        </Grid>

                        {myInfo?.userType === "SUPERADMIN" && (
                            <Grid size={{ xs: 12, md: 3 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Filter by Role</InputLabel>
                                    <Select
                                        value={selectedRole}
                                        label="Filter by Role"
                                        onChange={(e: SelectChangeEvent) => setSelectedRole(e.target.value)}
                                        sx={{ borderRadius: 3 }}
                                    >
                                        <MenuItem value="All">All Roles</MenuItem>
                                        <MenuItem value="Professional">Professionals</MenuItem>
                                        <MenuItem value="Corporate">Corporate</MenuItem>
                                        <MenuItem value="Admin">Admin</MenuItem>
                                        <MenuItem value="User">Users</MenuItem>
                                        <MenuItem value="e_mitra_user">E-Mitra</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" gap={1} height="100%" alignItems="center">
                                <Chip
                                    icon={<PersonIcon />}
                                    label={`Total Users: ${adminList.length}`}
                                    color="info"
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* User Table */}
                    <Slide direction="up" in timeout={600}>
                        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                            <TableContainer sx={{ overflow: 'auto' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>User ID</StyledTableCell>
                                            <StyledTableCell>First Name</StyledTableCell>
                                            <StyledTableCell>Last Name</StyledTableCell>
                                            <StyledTableCell>Email</StyledTableCell>
                                            <StyledTableCell>Contact</StyledTableCell>
                                            <StyledTableCell>User Role</StyledTableCell>
                                            <StyledTableCell>Status</StyledTableCell>
                                            <StyledTableCell>Professional Status</StyledTableCell>
                                            <StyledTableCell align="center">Action</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {displayedAdmins.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                                    <Typography variant="h6" color="text.secondary">No Users Found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            displayedAdmins.map((user: any) => (
                                                <StyledTableRow key={user.id}>
                                                    <TableCell>{user.id}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{user.firstName}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{user.lastName}</TableCell>
                                                    <TableCell>{user.email}</TableCell>
                                                    <TableCell>{user.contactNumber}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={formatUserType(user.userType)}
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={user.bverified ? "Verified" : "Pending"}
                                                            color={user.bverified ? "success" : "warning"}
                                                            size="small"
                                                            sx={{ fontWeight: 'bold' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.userType?.toUpperCase() === 'PROFESSIONAL' ? (
                                                            <Chip
                                                                label={user.beProfessionalVerified ? "Verified" : "Pending"}
                                                                color={user.beProfessionalVerified ? "success" : "warning"}
                                                                size="small"
                                                                sx={{ fontWeight: 'bold' }}
                                                            />
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box display="flex" gap={1} justifyContent="center">
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                color="info"
                                                                onClick={() => handleViewDetails(user)}
                                                                startIcon={<VisibilityIcon />}
                                                                sx={{ borderRadius: 2 }}
                                                            >
                                                                Details
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => handleEdit(user)}
                                                                sx={{ borderRadius: 2 }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            {user.userType?.toUpperCase() === 'PROFESSIONAL' && (
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    color="secondary"
                                                                    onClick={() => handleViewDocuments(user)}
                                                                    startIcon={<VisibilityIcon />}
                                                                    sx={{ borderRadius: 2 }}
                                                                >
                                                                    Docs
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </StyledTableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[20, 50, 100]}
                                component="div"
                                count={totalRecords}
                                rowsPerPage={pageSize}
                                page={page}
                                onPageChange={(event, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(event) => {
                                    setPageSize(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </Paper>
                    </Slide>

                    <Dialog
                        open={openDocsModal}
                        onClose={() => setOpenDocsModal(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle sx={{ backgroundColor: ColorData.primary.main, color: '#fff', mb: 2 }}>
                            Professional Documents
                        </DialogTitle>
                        <DialogContent>
                            {loadingDocs ? (
                                <Box display="flex" justifyContent="center" p={4}>
                                    <CircularProgress />
                                </Box>
                            ) : selectedUserDocs.length === 0 ? (
                                <Typography variant="body1" align="center" sx={{ py: 4 }}>
                                    No documents uploaded by this professional.
                                </Typography>
                            ) : (
                                <List>
                                    {selectedUserDocs.map((doc) => {
                                        const baseUrl = process.env.REACT_APP_BASE_URL || '';
                                        const fileUrl = doc.file.startsWith('http')
                                            ? doc.file
                                            : `${baseUrl}${doc.file.startsWith('/') ? '' : '/'}${doc.file}`;

                                        return (
                                            <ListItem
                                                key={doc.pkProfessionalCertificate}
                                                divider
                                                sx={{
                                                    '&:last-child': { borderBottom: 'none' },
                                                    py: 2
                                                }}
                                            >
                                                <ListItemText
                                                    primary={`Uploaded on ${new Date(doc.uploadedAt).toLocaleDateString()}`}
                                                    secondary="Professional Qualification Document"
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        color="primary"
                                                        onClick={() => window.open(fileUrl, '_blank')}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        color="error"
                                                        onClick={() => handleDeleteDocument(doc.pkProfessionalCertificate)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={() => setOpenDocsModal(false)} variant="outlined">
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openDetailsModal}
                        onClose={() => setOpenDetailsModal(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle sx={{ backgroundColor: ColorData.primary.main, color: '#fff', mb: 2 }}>
                            User Details
                        </DialogTitle>
                        <DialogContent dividers>
                            {selectedUserDetails && (
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    {Object.entries(selectedUserDetails)
                                        .filter(([key]) => {
                                            const k = key.toLowerCase();
                                            const uType = String(selectedUserDetails.userType || selectedUserDetails.UserType || "").toUpperCase();
                                            const isProf = uType === "PROFESSIONAL";
                                            const isCorp = uType === "CORPORATEUSER" || uType === "CORPORATE";

                                            if (k === "certificates" || k === "corporateorg") return false;

                                            // Hide professional agreement, ODR agreement & skill details for non-professional roles
                                            if (!isProf) {
                                                if (k.includes("skill") || k.includes("professional") || k.includes("odr") || k === "beprofessionalverified" || k === "isprofessionalagreementaccepted" || k === "isodragreementaccepted") {
                                                    return false;
                                                }
                                            }
                                            // Hide corporate details for non-corporate roles
                                            if (!isCorp && k.includes("corporate")) {
                                                return false;
                                            }
                                            return true;
                                        })
                                        .map(([key, value]) => {
                                            const isFullWidth = key.toLowerCase() === "skills" || key.toLowerCase() === "corporateorgdetails";
                                            return (
                                                <Grid size={isFullWidth ? { xs: 12 } : { xs: 12, sm: 6, md: 4 }} key={key}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                                                        {formatKey(key)}
                                                    </Typography>
                                                    <Box sx={{ wordBreak: 'break-word', color: '#334155', mt: 0.5 }}>
                                                        {renderValue(key, value)}
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={() => setOpenDetailsModal(false)} color="primary" variant="contained" sx={{ borderRadius: 2, px: 4 }}>
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Fade>
        </StyledContainer>
    );
};

export default AddAdminUser;
